<script lang="ts">
import type { Component, ExtractPublicPropTypes } from 'vue';
import { shallowReactive } from 'vue';

type ExcludeDollarProps<T> = {
  [K in keyof T as K extends `$${string}` ? never : K]: T[K];
};

type ExtractEventProps<T> = {
  [
    K in keyof T as K extends `on${Uppercase<string>}${string}`
      ? // eslint-disable-next-line @typescript-eslint/no-explicit-any
        NonNullable<T[K]> extends (...args: any[]) => any
        ? K
        : never
      : never
  ]: T[K];
};

type ComponentProps<C> =
  C extends Component<infer P>
    ? ExcludeDollarProps<ExtractPublicPropTypes<P>>
    : never;

type Rendering<C extends Component = Component> = {
  component: C;
  props?: ComponentProps<C>;
};

let uniqueId = 0;
const ids = new WeakMap<Rendering, string>();

export function showModal<C extends Component = Component>(
  renderingOrComponent: C | Rendering<C>,
) {
  const rendering =
    'component' in renderingOrComponent
      ? ({ ...renderingOrComponent } as Rendering)
      : ({ component: renderingOrComponent } as Rendering);

  if (rendering.props?.onHide === undefined) {
    rendering.props = {
      ...rendering.props,
      onHide: () => hide(),
    };
  }

  renderings.push(rendering);
  ids.set(rendering, String(++uniqueId));

  const hide = () => {
    const index = renderings.indexOf(rendering);
    if (~index) renderings.splice(index, 1);
  };

  return hide;
}

export function requestModal<
  C extends Component,
  Props extends ExtractEventProps<ComponentProps<C>>,
  K extends keyof Props,
>(renderingOrComponent: C | Rendering<C>, eventName: K) {
  type Params = Parameters<NonNullable<Props[typeof eventName]>>;
  return new Promise<
    Params['length'] extends 0
      ? void
      : Params['length'] extends 1
        ? Params[0]
        : Params
  >((resolve, reject) => {
    const rendering =
      'component' in renderingOrComponent
        ? ({ ...renderingOrComponent } as Rendering)
        : ({ component: renderingOrComponent } as Rendering);

    rendering.props = {
      ...rendering.props,
      onHide: () => {
        hide();
        reject(new Error('Canceled'));
      },
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      [eventName]: (...args: any[]) => {
        hide();
        if (args.length < 2) resolve(args[0]);
        // @ts-expect-error Multiple args
        else resolve(args);
      },
    };

    const hide = showModal(rendering);
  });
}

const renderings = shallowReactive<Rendering[]>([]);
</script>

<script setup lang="ts">
function onLeave(el: Element, done: () => void) {
  if (el instanceof HTMLElement && el.dataset.leaveDuration)
    setTimeout(done, +el.dataset.leaveDuration || 0);
  else done();
}
</script>

<template>
  <TransitionGroup tag="div" :css="false" @leave="onLeave">
    <component
      :is="rendering.component"
      v-for="rendering in renderings"
      :key="ids.get(rendering)"
      v-bind="rendering.props!"
    ></component>
  </TransitionGroup>
</template>
